import { NavLink } from 'react-router-dom';
import { PanelRightClose, PanelRightOpen, X } from 'lucide-react';
import { navFor } from '@/lib/navigation';
import { cn } from '@/lib/cn';
import { useLanguage } from '@/contexts/LanguageContext';
import type { Role } from '@/types/roles';
import { SidebarTooltip } from './SidebarTooltip';

/**
 * The nav, in three shapes from one list: expanded on desktop, a 72px icon
 * rail on desktop, and a drawer on mobile.
 *
 * The label is always the link's accessible name — in the rail it is an
 * `aria-label` plus a hover tooltip rather than visible text. A rail of icons
 * with no accessible names is unusable with a screen reader and untestable by
 * role, so the label never actually goes away; only its pixels do.
 *
 * State is owned by AppShell, not here: the drawer has to close when the route
 * changes, and the content offset has to follow the rail's width. Neither is
 * knowable from inside this component.
 *
 * Direction-aware. It used to be pinned to `right-0` with a note saying an LTR
 * locale would mean revisiting it — that locale now exists, so the edge, the
 * border and the off-canvas translation are all Tailwind rtl:/ltr: variants
 * reading <html dir>, which the language provider sets. No JS branch decides
 * where the sidebar lives.
 */
export function Sidebar({
  role,
  collapsed,
  onToggleCollapsed,
  drawerOpen,
  onCloseDrawer,
}: {
  role: Role;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  drawerOpen: boolean;
  onCloseDrawer: () => void;
}) {
  const { t, isRTL } = useLanguage();
  // The rail is a desktop idea. While the drawer is open the labels are shown
  // in full, because a 72px drawer would just be a worse top bar.
  const showRail = collapsed && !drawerOpen;

  return (
    <aside
      className={cn(
        'fixed bottom-0 top-16 z-50 flex w-64 flex-col border-border-subtle bg-white transition-all duration-300 ease-in-out',
        'rtl:right-0 rtl:border-l ltr:left-0 ltr:border-r',
        collapsed ? 'lg:w-[72px]' : 'lg:w-64',
        // Off-canvas on mobile unless opened; always on-canvas from lg up.
        // The off-canvas direction is the only thing that flips, so it flips in
        // JS rather than with an rtl: variant. Tailwind emits rtl:/ltr: rules
        // AFTER breakpoint rules, so `rtl:translate-x-full` would beat
        // `lg:translate-x-0` and leave the sidebar off-screen on desktop — which
        // is exactly what it did. Plain classes keep `lg:` winning.
        drawerOpen ? 'translate-x-0' : cn(isRTL ? 'translate-x-full' : '-translate-x-full', 'lg:translate-x-0')
      )}
    >
      <div className="flex h-12 items-center border-b border-border-subtle px-4 lg:hidden">
        <button
          type="button"
          onClick={onCloseDrawer}
          aria-label={t('navClose')}
          className="flex h-11 w-11 items-center justify-center rounded-md text-muted hover:bg-surface"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <nav className={cn('flex flex-1 flex-col gap-1 overflow-y-auto py-4', showRail ? 'px-2' : 'px-3')}>
        {navFor(role).map((item) => {
          const Icon = item.icon;
          const link = (
            <NavLink
              key={item.to}
              to={item.to}
              end
              onClick={onCloseDrawer}
              aria-label={t(item.labelKey)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md text-sm font-medium transition-colors',
                  // 44px minimum touch target on every row, rail or not.
                  showRail ? 'h-11 w-11 justify-center' : 'min-h-11 px-3 py-2.5',
                  isActive ? 'bg-primary text-primary-foreground' : 'text-ink hover:bg-surface'
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {showRail ? null : <span className="truncate">{t(item.labelKey)}</span>}
            </NavLink>
          );

          return showRail ? (
            <SidebarTooltip key={item.to} label={t(item.labelKey)} isRTL={isRTL}>{link}</SidebarTooltip>
          ) : (
            link
          );
        })}
      </nav>

      <div className={cn('hidden border-t border-border-subtle lg:flex', showRail ? 'justify-center p-2' : 'px-3 py-2')}>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? t('navExpand') : t('navCollapse')}
          className={cn(
            'flex items-center gap-2.5 rounded-md text-sm text-muted transition-colors hover:bg-surface hover:text-ink',
            showRail ? 'h-11 w-11 justify-center' : 'min-h-11 w-full px-3 py-2'
          )}
        >
          {collapsed ? (
            <PanelRightOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : (
            <PanelRightClose className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {showRail ? null : <span>{t('navCollapse')}</span>}
        </button>
      </div>
    </aside>
  );
}
